import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'antd';
import type { AwStateStageMember } from '@components/Modals/ApprovalModal/ApprovalModal/ApprovalModal';
import StageMembers from '@components/Modals/ApprovalModal/ApprovalModal/components/StagesStep/components/StageMembers';
import InviteReviewers from '@components/Modals/ApprovalModal/ApprovalModal/components/StagesStep/components/InviteReviewers';
import { CrossIcon } from '@icons';
import './StageMembersModal.less';

type StageMembersModalProps = {
  isOpen: boolean;
  stageNumber: number;
  campaignId?: string;
  ownerEmail: string;
  members: AwStateStageMember[];
  allDecisionsSubmittedChecked: boolean;
  hasDominatingDecisions: boolean;
  stageFinished: boolean;
  onCloseModal: () => void;
  onUpdateMembers: (members: AwStateStageMember[]) => void;
};

export default function StageMembersModal({
  isOpen,
  stageNumber,
  ownerEmail,
  members,
  campaignId,
  allDecisionsSubmittedChecked,
  hasDominatingDecisions,
  stageFinished,
  onCloseModal,
  onUpdateMembers
}: StageMembersModalProps) {
  const [localMembers, setLocalMembers] = useState(members);
  const isModified = useMemo(() => {
    return JSON.stringify(localMembers) !== JSON.stringify(members);
  }, [localMembers, members]);

  useEffect(() => {
    if (!isOpen) return;
    setLocalMembers(members);
  }, [members, isOpen, setLocalMembers]);

  const onSave = useCallback(() => {
    onUpdateMembers(localMembers);
    onCloseModal();
  }, [localMembers, onUpdateMembers, onCloseModal]);

  return (
    <Modal
      open={isOpen}
      footer={null}
      centered={true}
      width={755}
      destroyOnClose
      zIndex={1010}
      onCancel={onCloseModal}
      className="stage-members-modal"
    >
      <CrossIcon
        className="stage-members-modal-btn-close"
        onClick={onCloseModal}
      />
      <div id="AW_STAGE_REVIEWERS">
        <div className="stage-members-modal_title">
          Stage {stageNumber} reviewers
        </div>
        <div className="stage-members-modal_text">
          Add anyone inside or outside your organization to review this asset
          and set their permissions.
        </div>
        {campaignId && (
          <InviteReviewers
            members={localMembers}
            campaignId={campaignId}
            onUpdateMembers={setLocalMembers}
            allDecisionsSubmittedChecked={allDecisionsSubmittedChecked}
            hasDominatingDecisions={hasDominatingDecisions}
            stageFinished={stageFinished}
          />
        )}
        <StageMembers
          onUpdateMembers={setLocalMembers}
          members={localMembers}
          ownerEmail={ownerEmail}
        />
        <div className="approval-step_buttons">
          <Button type="text" onClick={onCloseModal}>
            Cancel
          </Button>
          <Button
            type="primary"
            disabled={!isModified}
            style={{
              minWidth: 130
            }}
            onClick={onSave}
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}
